/**
 * v1.14.61.1.0 — Label umane per il Wizard v2 Bulk.
 *
 * Mappa chiave tecnica → label human-readable italiana, basata sul
 * master schema tariffario Reale Mutua Composita Reale Mod. 3063 INC
 * (vedi /app/memory/quoter_composita_reale_master_schema.md +
 * /app/memory/wizard_v2_tariff_mapping.md). Le chiavi tecniche restano
 * in state/DB; le label vengono usate solo per il rendering UI.
 *
 * Tooltip: testi user-friendly che spiegano i campi tariffari (§).
 */

export const WIZARD_LABELS = {
  // ═══════════════════════════════════════════════════════════════════
  // STEP 1 — INCENDIO (§1.12 + §3.6.1/2)
  // ═══════════════════════════════════════════════════════════════════
  incendio: {
    title: "Incendio & Sub-coperture",
    subtitle: "Seleziona quali immobili includere nella copertura Incendio e specifica i valori per immobile. Le sub-coperture A-W si applicano globalmente.",
    columns: {
      valore_fabbricato_eur: "Valore Fabbricato €",
      valore_contenuto_eur: "Valore Contenuto €",
    },
    sub_coperture: {
      A_beni_speciali: "Beni assicurati a condizioni speciali",
      B_demolizione: "Spese di demolizione e sgombero",
      C_ricorso_terzi: "Ricorso terzi da incendio",
      D_rischio_locativo_auto: "Rischio locativo",
      E_fenomeno_elettrico: "Fenomeno elettrico",
      F_indennita_aggiuntiva_10pct: "Indennità aggiuntiva (10% sulle partite)",
      M_acqua_piovana: "Acqua piovana (limite indennizzo)",
      N_spargimenti_acqua: "Spargimenti d'acqua da condotte",
      V_rottura_lastre: "Rottura lastre e cristalli",
      W_perdita_pigioni: "Perdita pigioni",
    },
    sub_hints: {
      A_beni_speciali: "Tasso 2,50‰ — Valore PRA dei beni a condizioni speciali (es. quadri, oggetti d'arte, gioielli)",
      B_demolizione: "Valore PRA — 20‰ per Civile/Agricolo/ARC, 2‰ per CPI/RV",
      C_ricorso_terzi: "Tasso = tasso fabbricato (Civile/ARC/Agricolo) o 8/10 tasso contenuto (CPI/Rischi Vari) — verbatim §3.6.1/2 C",
      D_rischio_locativo_auto: "Attivata automaticamente se l'immobile non è di proprietà del contraente",
      E_fenomeno_elettrico: "Tasso 15‰ per Civile/Agricolo/ARC, 17‰ per CPI/RV",
      F_indennita_aggiuntiva_10pct: "+10% sull'elenco partite assicurate (auto-applicato dal motore)",
      M_acqua_piovana: "Premio fisso per limite: €50 (lim. 10k) / €100 (25k) / €150 (50k) — verbatim §3.6.1 M / §3.6.2 N",
      N_spargimenti_acqua: "Tasso 0,10‰ sulla somma fabb+cont — verbatim §3.6.1 N / §3.6.2 O",
      V_rottura_lastre: "Tasso 15‰ — Valore PRA cristalli, vetrate e lastre",
      W_perdita_pigioni: "Tasso 3‰ — Annualità di locazione persa a seguito sinistro",
    },
  },

  // ═══════════════════════════════════════════════════════════════════
  // STEP 2 — FURTO (§3.1 forme + §3.6.3/4 sub-cop)
  // ═══════════════════════════════════════════════════════════════════
  furto: {
    title: "Furto & Sub-coperture",
    subtitle: "Seleziona immobili e forma assicurativa (PRA, Valore Intero, Globale/Analitica solo Civili). Le sub-coperture si applicano per singolo immobile.",
    forme: {
      pra: "Primo Rischio Assoluto (PRA)",
      valore_intero: "Valore Intero (VI)",
      globale: "Globale §3.1.1 (solo Civili)",
      analitica: "Analitica §3.1.2 (solo Civili)",
    },
    columns: {
      forma: "Forma assicurativa",
      valore_eur: "Valore assicurato €",
    },
    sub_coperture_civ_agr_arc: {
      A_archivi: "Archivi e documenti",
      B_scippo_generico: "Scippo e rapina (generico)",
      C_scippo_albergo: "Scippo clienti albergo",
      D_trasporto_valori: "Trasporto valori",
      E_bestiame_aperto: "Bestiame all'aperto",
      F_macchine_aperto: "Macchine agricole all'aperto",
      G_veicoli_albergo: "Veicoli clienti albergo",
      H_allarme: "Impianto di allarme attivo",
      I_cassaforte: "Valori in cassaforte",
    },
    sub_coperture_cpi_rv: {
      A_beni_terzi: "Beni presso terzi",
      B_esposizioni: "Esposizioni e fiere",
      C_beni_aperto: "Beni all'aperto",
      D_indennita_10pct: "Indennità aggiuntiva (10%)",
      E_trasporto_valori: "Trasporto valori",
      F_veicoli_aperto: "Veicoli all'aperto",
      G_allarme: "Impianto di allarme attivo",
      H_cassaforte_20: "Valori in cassaforte (20‰)",
    },
    sub_hints: {
      A_archivi: "Tasso 5‰ — Civ/ARC/Agr",
      B_scippo_generico: "Tasso 20‰ — Civ/ARC/Agr",
      C_scippo_albergo: "Tasso 9‰ — Solo ARC alberghi",
      D_trasporto_valori: "Tasso 20‰ PRA — Civ/ARC/Agr",
      E_bestiame_aperto: "Tassi differenziati per classe (stalla abituale vs non-abituale) — Solo Agricolo",
      F_macchine_aperto: "Tassi differenziati VI/PRA per classe — Solo Agricolo",
      G_veicoli_albergo: "Tasso 19‰ PRA — Solo ARC alberghi",
      H_allarme: "Sconto non quantificato — applicato in fase emissione",
      I_cassaforte: "Tasso 10‰ PRA — Valori in cassaforte",
      A_beni_terzi: "+20% premio contenuto — Solo CPI/RV",
      B_esposizioni: "+5% premio contenuto — Solo CPI/RV",
      C_beni_aperto: "+20% premio contenuto — Solo CPI/RV",
      D_indennita_10pct: "+10% — Solo CPI/RV",
      E_trasporto_valori: "Tasso 20‰ — Solo CPI/RV",
      F_veicoli_aperto: "Tassi VI/PRA per classe — Solo CPI/RV",
      G_allarme: "Sconto non quantificato — Solo CPI/RV",
      H_cassaforte_20: "Tasso 20‰ PRA — Solo CPI/RV",
    },
  },

  // ═══════════════════════════════════════════════════════════════════
  // STEP 3 — RC FABBRICATO (§3.5 verbatim pag 64)
  // ═══════════════════════════════════════════════════════════════════
  rcf: {
    title: "Responsabilità Civile del Fabbricato",
    subtitle: "Seleziona codice rischio §3.5 + massimale per singolo immobile. Add-on RC FULL globale.",
    columns: {
      codice: "Codice rischio §3.5",
      massimale_eur: "Massimale €",
      full: "RC FULL",
    },
    codici: {
      "20111": "Fabbricato IN condominio (≥3/4 abitazioni/uffici) o ospedale/clinica",
      "20112": "Fabbricato NON in condominio (≥3/4 abitazioni/uffici) o ospedale/clinica",
      "20113": "Fabbricato con ≥1/4 destinato a pinacoteca/museo/biblioteca/teatro/cinema/sala",
      "20114": "Fabbricato ad uso rurale",
      "20115": "Industriale/commerciale del contraente | chiesa/scuola/palestra/termale/caserma/albergo/B&B",
      "20116": "Industriale/commerciale esercitato DA TERZI",
      "20117": "Diverso dai precedenti (Rischio Direzionale — autorizzazione richiesta)",
    },
    massimali: {
      500000: "€ 500.000",
      1000000: "€ 1.000.000",
      2500000: "€ 2.500.000",
      5000000: "€ 5.000.000",
    },
    full_label: "RC FULL — Estensione di copertura RC Fabbricato",
    full_hint: "+10% Civile/Agricolo/ARC, +20% CPI/RV (auto-applicato dal motore).",
  },

  // ═══════════════════════════════════════════════════════════════════
  // STEP 4 — EVENTI ATMOSFERICI (sub-cop incendio I/L/M/N/O/P/Q/Z)
  // ═══════════════════════════════════════════════════════════════════
  atmosferici: {
    title: "Eventi Atmosferici",
    subtitle: "Sub-coperture per singolo immobile. Zona V&G (Sud/Centro/Nord) determinata dal sistema. Premi fissi per acqua piovana/gelo/dispersione.",
    sub_coperture: {
      I_vento_grandine: "Vento e grandine",
      L_vg_fragili: "V&G su parti fragili (dipende da I)",
      M_acqua_piovana: "Acqua piovana",
      N_spargimenti_acqua: "Spargimenti d'acqua",
      O_gelo: "Gelo",
      P_ricerca_guasti: "Ricerca guasti e perdite",
      Q_dispersione: "Dispersione liquidi",
      Z_sovraccarico_neve: "Sovraccarico neve (dipende da I)",
    },
    sub_hints: {
      I_vento_grandine: "Tasso zonale: 0,15‰ Sud / 0,20‰ Centro / 0,30‰ Nord",
      L_vg_fragili: "Stesso tasso di I — richiede I attivata",
      M_acqua_piovana: "Premio fisso: €50 (lim. 10k) / €100 (lim. 25k) / €150 (lim. 50k)",
      N_spargimenti_acqua: "Tasso 0,10‰",
      O_gelo: "Premio fisso: €25 (lim. 10k) / €50 (lim. 25k) / €80 (lim. 50k)",
      P_ricerca_guasti: "Tasso 0,10‰ sul fabbricato",
      Q_dispersione: "Premio fisso: €50 (lim. 10k) / €100 (lim. 25k) / €150 (lim. 50k)",
      Z_sovraccarico_neve: "Tasso 0,10‰ — richiede I attivata",
    },
    limit_options_m_q: [
      { value: 10000, label: "€ 10.000" },
      { value: 25000, label: "€ 25.000" },
      { value: 50000, label: "€ 50.000" },
    ],
    limit_options_o: [
      { value: 10000, label: "€ 10.000" },
      { value: 25000, label: "€ 25.000" },
      { value: 50000, label: "€ 50.000" },
    ],
  },

  // ═══════════════════════════════════════════════════════════════════
  // STEP 5 — EVENTI CATASTROFALI (J Terremoto + K Inondazione)
  // ═══════════════════════════════════════════════════════════════════
  catastrofali: {
    title: "Eventi Catastrofali",
    subtitle: "Terremoto e Inondazione sono Rischi Direzionali: richiedono autorizzazione Reale Mutua + tasso negoziato manuale.",
    rd_warning: "Le coperture catastrofali Terremoto e Inondazione sono soggette ad autorizzazione direzionale Reale Mutua. Senza autorizzazione la quotazione resta in stato 'da approvare'.",
    sub_coperture: {
      J_terremoto: "Terremoto",
      K_inondazione: "Inondazione e alluvione",
    },
    sub_hints: {
      J_terremoto: "Rischio Direzionale — tasso ‰ negoziato in base a zona sismica e tipologia costruttiva",
      K_inondazione: "Rischio Direzionale — tasso ‰ negoziato in base a zona idrogeologica",
    },
  },

  // ═══════════════════════════════════════════════════════════════════
  // STEP 6 — ALTRE SUB-COPERTURE OPZIONALI
  // ═══════════════════════════════════════════════════════════════════
  altre: {
    title: "Altre Sub-coperture",
    subtitle: "Garanzie aggiuntive opzionali (eventi sociopolitici, terrorismo, refrigerazione, ecc.).",
    sub_coperture: {
      G_sociopolitici: "Eventi sociopolitici e tumulti",
      H_terrorismo: "Terrorismo e sabotaggio",
      R_refrigerazione: "Merci in refrigerazione",
      S_folgorazione_bestiame: "Folgorazione bestiame",
      T_fermentazione_foraggio: "Fermentazione foraggio",
      MC_maggiori_costi: "Maggiori costi di gestione",
    },
    sub_hints: {
      G_sociopolitici: "Tasso 0,10‰",
      H_terrorismo: "Tasso 0,05‰",
      R_refrigerazione: "Da quotazione — richiede input manuale (premio negoziato)",
      S_folgorazione_bestiame: "Tasso 0,20‰ sul bestiame — Solo Agricolo",
      T_fermentazione_foraggio: "Tasso 3‰ sul foraggio — Solo Agricolo",
      MC_maggiori_costi: "Da quotazione — richiede input manuale (premio negoziato)",
    },
  },
};

// ═══════════════════════════════════════════════════════════════════════
// TOOLTIPS — definizioni tecniche tariffarie per i campi del wizard
// ═══════════════════════════════════════════════════════════════════════

export const FIELD_TOOLTIPS = {
  valore_fabbricato_eur: {
    title: "Valore Fabbricato (§1.12.1)",
    body: "È il valore di RICOSTRUZIONE integrale dell'edificio: muri portanti, tetto, infissi fissi, impianti incorporati (idrico, elettrico, riscaldamento), pavimenti, tinteggiature. NON include il contenuto interno (mobilio, attrezzature). Per beni in attività: valore a nuovo. Per beni inattivi: valore allo stato d'uso.",
    ref: "Tariffa Reale Mutua Composita Reale §1.12.1",
  },
  valore_contenuto_eur: {
    title: "Valore Contenuto (§1.12.2)",
    body: "È il valore di RISARCIMENTO di tutto ciò che si trova DENTRO il fabbricato: mobilio, arredo, elettronica, macchinari, attrezzature, merci, scorte, beni di proprietà o in deposito. NON include il fabbricato stesso (muri, tetto, impianti incorporati). Per oggetti d'arte/gioielli: valore commerciale. Per beni di produzione/commercio: valore a nuovo o commerciale a seconda della partita.",
    ref: "Tariffa Reale Mutua Composita Reale §1.12.2",
  },
  massimale_rcf: {
    title: "Massimale RC Fabbricato (§3.5)",
    body: "È il LIMITE MASSIMO di responsabilità che Reale Mutua si impegna a coprire per i danni causati a terzi (persone, cose) dall'immobile assicurato. Quattro massimali standard: €500.000, €1.000.000, €2.500.000, €5.000.000.",
    ref: "Tariffa Reale Mutua Composita Reale §3.5",
  },
  valore_furto_pra: {
    title: "Valore Furto — Primo Rischio Assoluto (§3.1)",
    body: "Nel Primo Rischio Assoluto (PRA) si assicura un valore FISSO indipendentemente dal valore totale dei beni: in caso di sinistro si viene risarciti fino a quel valore senza regola proporzionale. Nel Valore Intero (VI) si assicura il valore totale e si applica la regola proporzionale.",
    ref: "Tariffa Reale Mutua Composita Reale §3.1.1/§3.1.2",
  },
  forma_furto: {
    title: "Forma assicurativa Furto (§3.1)",
    body: "PRA (Primo Rischio Assoluto): si paga sul valore dichiarato, no proporzionale. VI (Valore Intero): si copre il totale, con regola proporzionale in caso di sotto-assicurazione. Globale (§3.1.1) e Analitica (§3.1.2) sono forme dedicate ai rischi civili.",
    ref: "Tariffa Reale Mutua Composita Reale §3.1",
  },
  codice_rcf: {
    title: "Codice rischio RC Fabbricato (§3.5)",
    body: "Codice tariffario (20111-20117) che identifica la destinazione d'uso prevalente dell'immobile. Determina il tasso applicato. Il codice 20117 è un Rischio Direzionale e richiede autorizzazione Reale Mutua.",
    ref: "Tariffa Reale Mutua Composita Reale §3.5 pag. 64",
  },
  // Sub-coperture Incendio
  A_beni_speciali: {
    title: "Beni a condizioni speciali (§3.6.1/2 A)",
    body: "Beni di particolare valore (quadri, sculture, oggetti d'arte, gioielli, collezioni) per cui si vuole una somma assicurata aggiuntiva specifica. Inserisci il valore PRA che vuoi tutelare: in caso di sinistro vieni risarcito fino a questo importo. Tasso 2,50‰.",
    ref: "Tariffa Reale Mutua §3.6.1/2 A",
  },
  B_demolizione: {
    title: "Spese di demolizione e sgombero (§3.6.1/2 B)",
    body: "Costi accessori al sinistro: demolizione di strutture compromesse, sgombero macerie, trasporto rifiuti in discarica. Si consiglia 10-15% del valore fabbricato. Tasso 20‰ Civile/Agricolo/ARC, 2‰ CPI/RV.",
    ref: "Tariffa Reale Mutua §3.6.1/2 B",
  },
  C_ricorso_terzi: {
    title: "Ricorso terzi da incendio (§3.6.1/2 C)",
    body: "Responsabilità del proprietario per danni causati a terzi (vicini, locatari, persone) da un incendio originato nel suo fabbricato. Inserire il valore massimo (somma assicurata PRA) che si vuole tutelare. Il tasso è automatico in base al settore dell'immobile: tasso del fabbricato per Civile / ARC / Agricolo (×1.0), oppure 8/10 del tasso del contenuto per Commercio-Piccola Industria / Rischi Vari (×0.8).",
    ref: "Tariffa Reale Mutua §3.6.1/2 C",
  },
  E_fenomeno_elettrico: {
    title: "Fenomeno elettrico (§3.6.1/2 E)",
    body: "Danni causati da cortocircuiti, sovratensioni, scariche elettriche su apparecchi e impianti (TV, frigoriferi, elettrodomestici, server, quadri elettrici). Inserire il valore PRA: in caso di sinistro elettrico vieni risarcito fino a questo importo. Tasso 15‰ Civile/Agricolo/ARC, 17‰ CPI/RV.",
    ref: "Tariffa Reale Mutua §3.6.1/2 E",
  },
  V_rottura_lastre: {
    title: "Rottura lastre e cristalli (§3.6.1/2 V)",
    body: "Danni accidentali a vetrate, vetrine, cristalli, lastre fisse, specchi murali. Tipico per attività commerciali con vetrine ampie o uffici con vetrate panoramiche. Inserire il valore PRA delle lastre da assicurare. Tasso 15‰.",
    ref: "Tariffa Reale Mutua §3.6.1/2 V",
  },
  W_perdita_pigioni: {
    title: "Perdita pigioni (§3.6.1/2 W)",
    body: "Per i locatori: se l'immobile diventa inagibile dopo un sinistro, copre il mancato incasso del canone di locazione fino al ripristino. Inserire l'annualità di locazione che si vuole tutelare. Tasso 3‰.",
    ref: "Tariffa Reale Mutua §3.6.1/2 W",
  },
  M_acqua_piovana: {
    title: "Acqua piovana (§3.6.1 M / §3.6.2 N)",
    body: "Danni da infiltrazione d'acqua piovana attraverso tetti, terrazze, lucernari, grondaie ostruite. Premio fisso per limite indennizzo scelto: €50 per limite €10.000 / €100 per €25.000 / €150 per €50.000.",
    ref: "Tariffa Reale Mutua §3.6.1 M / §3.6.2 N",
  },
  N_spargimenti_acqua: {
    title: "Spargimenti d'acqua da condotte (§3.6.1 N / §3.6.2 O)",
    body: "Danni da rottura accidentale di condotte idriche, scarichi, impianti di climatizzazione, lavatrici. Coperto come spargimento del contenuto interno. Tasso 0,10‰ applicato sulla somma assicurata fabbricato+contenuto.",
    ref: "Tariffa Reale Mutua §3.6.1 N / §3.6.2 O",
  },
  MC_maggiori_costi: {
    title: "Maggiori costi di gestione (§3.6.x MC)",
    body: "Spese aggiuntive sostenute per mantenere l'attività dopo un sinistro: noleggio locali temporanei, spostamento attrezzature, costi extra di logistica. È una garanzia 'da quotazione': l'agenzia definisce manualmente il premio in base alla complessità.",
    ref: "Tariffa Reale Mutua §3.6 MC",
  },
  // Furto
  furto_A_archivi: {
    title: "Archivi e documenti (§3.6.3 A)",
    body: "Furto/danneggiamento di archivi, documenti contabili, contratti, registri. Inserire il valore PRA dei documenti. Tasso 5‰.",
    ref: "Tariffa Reale Mutua §3.6.3 A",
  },
  furto_D_trasporto_valori: {
    title: "Trasporto valori (§3.6.3 D)",
    body: "Furto durante il trasporto di denaro, titoli, gioielli dal/al fabbricato (es. trasferimento incassi in banca). Inserire il valore PRA trasportato. Tasso 20‰ PRA.",
    ref: "Tariffa Reale Mutua §3.6.3 D",
  },
  furto_G_veicoli_albergo: {
    title: "Veicoli clienti albergo (§3.6.3 G)",
    body: "Furto di veicoli dei clienti parcheggiati nelle aree di pertinenza dell'albergo. Solo per ARC alberghi. Inserire il valore PRA totale dei veicoli. Tasso 19‰ PRA.",
    ref: "Tariffa Reale Mutua §3.6.3 G",
  },
  furto_I_cassaforte: {
    title: "Valori in cassaforte (§3.6.3 I)",
    body: "Furto di denaro, titoli, valori contenuti in cassaforte fissa con caratteristiche conformi. Inserire il valore PRA dei valori custoditi. Tasso 10‰ PRA.",
    ref: "Tariffa Reale Mutua §3.6.3 I",
  },
  // Catastrofali
  J_terremoto: {
    title: "Terremoto (§ Rischio Direzionale)",
    body: "Danni da scossa sismica al fabbricato e/o contenuto. È un Rischio Direzionale: richiede autorizzazione Reale Mutua e tasso negoziato in base a zona sismica e tipologia costruttiva. Inserire il valore PRA da tutelare in caso di evento sismico.",
    ref: "Tariffa Reale Mutua — R.D. Terremoto",
  },
  K_inondazione: {
    title: "Inondazione e alluvione (§ Rischio Direzionale)",
    body: "Danni da alluvione, esondazione, inondazione al fabbricato e/o contenuto. È un Rischio Direzionale: richiede autorizzazione e tasso negoziato in base a zona idrogeologica. Inserire il valore PRA da tutelare.",
    ref: "Tariffa Reale Mutua — R.D. Inondazione",
  },
};
